import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Award,
  BarChart3,
  CheckCircle2,
  Crown,
  Filter,
  Flame,
  Loader2,
  PieChart as PieChartIcon,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import MedaHero from "@/components/MedaHero";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const PERIODS = [
  { v: "today", l: "Today" },
  { v: "7d", l: "7 days" },
  { v: "30d", l: "30 days" },
  { v: "90d", l: "90 days" },
  { v: "all", l: "All time" },
];
const COUNTRY_COLORS = [
  "#0A2540", "#C9A961", "#34D399", "#F472B6", "#60A5FA",
  "#FBBF24", "#A78BFA", "#F87171", "#22D3EE", "#84CC16",
  "#FB923C", "#94A3B8",
];
const TEMP_COLORS = {
  Hot: "#EF4444",
  Warm: "#F59E0B",
  Cool: "#60A5FA",
  Cold: "#94A3B8",
};

function HeadlineCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-[0_1px_0_rgba(10,37,64,0.04)]"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md ${accent}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="font-display mt-3 text-3xl font-bold tracking-tight text-[#0A2540]">
        {value ?? "—"}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-[#0A2540]">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function FunnelChart({ funnel }) {
  if (!funnel?.length) return <div className="py-6 text-center text-sm text-slate-500">No data</div>;
  const max = funnel[0]?.count || 1;
  return (
    <div className="space-y-3">
      {funnel.map((s, i) => {
        const widthPct = (s.count / max) * 100;
        const isLast = i === funnel.length - 1;
        return (
          <div key={s.stage} data-testid={`funnel-${s.stage.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-[#0A2540]">{s.stage}</span>
              <span className="tabular-nums text-slate-500">
                {s.count.toLocaleString()}
                {i > 0 && (
                  <span className="ml-2 inline-block min-w-[3rem] rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                    {s.pct_of_prev}% kept
                  </span>
                )}
              </span>
            </div>
            <div className="h-7 w-full overflow-hidden rounded-md bg-slate-100">
              <div
                className={`h-full rounded-md transition-all ${
                  isLast ? "bg-emerald-500" : "bg-[#0A2540]"
                }`}
                style={{ width: `${widthPct}%`, minWidth: s.count > 0 ? "2px" : "0" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const LQ_COLORS = {
  Untouched: "bg-slate-400",
  "Not Connected": "bg-amber-500",
  Cold: "bg-sky-500",
  Warm: "bg-orange-500",
  Referred: "bg-violet-500",
};

function LeadQualityCard({ lq }) {
  const stages = lq?.stages || [];
  const total = lq?.total || 0;
  return (
    <div className="space-y-3" data-testid="lead-quality-stages">
      <div className="flex items-center justify-between rounded-md bg-[#0A2540]/5 px-3 py-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
          All (Total leads)
        </span>
        <span className="font-display text-xl font-bold text-[#0A2540]" data-testid="lq-total">
          {total.toLocaleString()}
        </span>
      </div>
      {stages.map((s) => {
        const pct = total ? Math.round((100 * s.count) / total) : 0;
        return (
          <div key={s.stage} data-testid={`lq-${s.stage.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-[#0A2540]">
                {s.stage}
                {s.stage === "Referred" && (
                  <span className="ml-1 text-[10px] text-slate-400">(Qualified)</span>
                )}
              </span>
              <span className="tabular-nums text-slate-500">
                {s.count.toLocaleString()}
                <span className="ml-2 inline-block min-w-[2.5rem] rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                  {pct}%
                </span>
              </span>
            </div>
            <div className="h-6 w-full overflow-hidden rounded-md bg-slate-100">
              <div
                className={`h-full rounded-md transition-all ${LQ_COLORS[s.stage] || "bg-[#0A2540]"}`}
                style={{ width: `${pct}%`, minWidth: s.count > 0 ? "2px" : "0" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CloseReasonsCard({ reasons }) {
  const rows = reasons || [];
  if (!rows.length)
    return (
      <div className="py-8 text-center text-sm text-slate-400" data-testid="close-reasons-empty">
        No closed leads in this period yet.
      </div>
    );
  const total = rows.reduce((a, r) => a + (r.count || 0), 0) || 1;
  const COLORS = [
    "bg-rose-500", "bg-orange-500", "bg-amber-500",
    "bg-violet-500", "bg-sky-500", "bg-slate-400",
  ];
  return (
    <div className="space-y-3" data-testid="close-reasons-list">
      {rows.map((r, i) => {
        const pct = Math.round((100 * r.count) / total);
        return (
          <div key={r.reason}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-[#0A2540]">{r.reason}</span>
              <span className="tabular-nums text-slate-500">
                {r.count}
                <span className="ml-2 inline-block min-w-[2.5rem] rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                  {pct}%
                </span>
              </span>
            </div>
            <div className="h-6 w-full overflow-hidden rounded-md bg-slate-100">
              <div
                className={`h-full rounded-md transition-all ${COLORS[i % COLORS.length]}`}
                style={{ width: `${pct}%`, minWidth: r.count > 0 ? "2px" : "0" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WaTemplateUsage({ period }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let off = false;
    setLoading(true);
    api
      .get("/admin/analytics/wa-templates", { params: { period } })
      .then((r) => !off && setD(r.data))
      .catch(() => !off && setD(null))
      .finally(() => !off && setLoading(false));
    return () => { off = true; };
  }, [period]);

  return (
    <div className="mt-6">
      <ChartCard
        title="WhatsApp template usage"
        subtitle="Click-to-WhatsApp templates sent & reply rates"
        testid="card-wa-template-usage"
      >
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400">
            <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !d || d.total_sends === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="wa-usage-empty">
            No template sends in this window.
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-6 text-sm">
              <div><span className="font-display text-2xl font-bold text-[#0A2540]">{d.total_sends}</span> <span className="text-slate-500">sends</span></div>
              <div><span className="font-display text-2xl font-bold text-[#0A2540]">{d.total_replies}</span> <span className="text-slate-500">replies</span></div>
              <div><span className="font-display text-2xl font-bold text-emerald-600">{d.reply_rate}%</span> <span className="text-slate-500">reply rate</span></div>
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              <UsageTable title="By counsellor" rows={d.by_counsellor} testid="wa-usage-by-counsellor" />
              <UsageTable title="By template" rows={d.by_template} testid="wa-usage-by-template" />
            </div>
          </div>
        )}
      </ChartCard>
    </div>
  );
}

function UsageTable({ title, rows, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-slate-400">No data</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1 truncate text-sm text-[#0A2540]">{r.name}</div>
              <div className="text-xs text-slate-500">{r.sends} sent</div>
              <div className="w-16 text-right text-xs font-medium text-emerald-600">{r.reply_rate}%</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function AnalyticsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/admin/analytics", { params: { period } })
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  if (user && user.role !== "admin" && user.role !== "super_admin") {
    nav("/admin", { replace: true });
    return null;
  }

  const headline = data?.headline;

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Page header */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 pl-16 sm:px-6 md:pl-6 lg:px-10">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#0A2540]" />
            <span className="font-display text-base font-bold tracking-tight text-[#0A2540]">
              Analytics
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-xs">
              {PERIODS.map((p) => (
                <button
                  key={p.v}
                  onClick={() => setPeriod(p.v)}
                  className={`rounded px-2.5 py-1 transition-colors ${
                    period === p.v
                      ? "bg-[#0A2540] text-white"
                      : "text-slate-600 hover:text-[#0A2540]"
                  }`}
                  data-testid={`period-${p.v}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
        <MedaHero />
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A2540]">
          Performance Insights
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {user?.tenant_name && <>Consultancy: {user.tenant_name} · </>}
          {data ? `${headline?.total_leads ?? 0} leads in window` : "Loading…"}
        </p>

        {loading && (
          <div className="flex items-center justify-center py-20 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Crunching numbers…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Headline KPIs */}
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <HeadlineCard
                icon={Users}
                label="Total leads"
                value={headline.total_leads}
                accent="bg-[#0A2540]/10 text-[#0A2540]"
                testid="kpi-leads"
              />
              <HeadlineCard
                icon={CheckCircle2}
                label="Quiz completion"
                value={`${headline.test_completion_rate}%`}
                sub="of registered"
                accent="bg-amber-100 text-amber-700"
                testid="kpi-completion"
              />
              <HeadlineCard
                icon={TrendingUp}
                label="Avg test score"
                value={headline.avg_test_score}
                sub="out of 18"
                accent="bg-violet-100 text-violet-700"
                testid="kpi-avg-score"
              />
              <HeadlineCard
                icon={Award}
                label="Close rate"
                value={`${headline.close_rate}%`}
                sub="leads → closed"
                accent="bg-emerald-100 text-emerald-700"
                testid="kpi-close"
              />
            </div>

            {/* Lead Quality + Close Reasons */}
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {data.lead_quality && (
                <ChartCard
                  title="Lead Quality"
                  subtitle={`Non-MBBS leads by stage · ${data.lead_quality.total} total`}
                  testid="card-lead-quality"
                >
                  <LeadQualityCard lq={data.lead_quality} />
                </ChartCard>
              )}
              <ChartCard
                title="Close Reasons"
                subtitle="Why leads were closed (Leads + Counselings)"
                testid="card-close-reasons"
              >
                <CloseReasonsCard reasons={data.close_reasons} />
              </ChartCard>
            </div>

            {/* Funnel */}
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <ChartCard
                title="Conversion funnel"
                subtitle="Drop-off across the student journey"
                testid="card-funnel"
              >
                <FunnelChart funnel={data.funnel} />
              </ChartCard>

              {data.sales_funnel && (
                <ChartCard
                  title="Sales pipeline"
                  subtitle="New → Contacted → Follow-up → Docs → Zoom → Visited → Enrolled"
                  testid="card-sales-funnel"
                >
                  <FunnelChart funnel={data.sales_funnel} />
                </ChartCard>
              )}

              {/* UTM bar */}
              <ChartCard
                title="Top traffic sources"
                subtitle="Leads + completion rate by UTM source"
                testid="card-utm"
              >
                {data.utm_breakdown.length === 0 ? (
                  <div className="py-10 text-center text-sm text-slate-500">No source data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.utm_breakdown} layout="vertical" margin={{ left: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" stroke="#64748B" fontSize={11} />
                      <YAxis dataKey="source" type="category" stroke="#64748B" fontSize={11} width={80} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="leads" fill="#0A2540" name="Leads" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="completed" fill="#C9A961" name="Completed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {/* Country pie */}
              <ChartCard
                title="Country preference"
                subtitle="Where students want to study MBBS"
                testid="card-countries"
              >
                {data.countries.length === 0 ? (
                  <div className="py-10 text-center text-sm text-slate-500">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={data.countries}
                        dataKey="count"
                        nameKey="country"
                        cx="50%"
                        cy="50%"
                        outerRadius={95}
                        innerRadius={45}
                        label={({ country, percent }) =>
                          percent > 0.05 ? `${country} ${(percent * 100).toFixed(0)}%` : ""
                        }
                        labelLine={false}
                      >
                        {data.countries.map((_, i) => (
                          <Cell key={i} fill={COUNTRY_COLORS[i % COUNTRY_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* Score histogram */}
              <ChartCard
                title="NEET score distribution"
                subtitle="Lead temperature across NEET score buckets"
                testid="card-histogram"
              >
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.score_histogram}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="bucket" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Hot" stackId="a" fill={TEMP_COLORS.Hot} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Warm" stackId="a" fill={TEMP_COLORS.Warm} />
                    <Bar dataKey="Cool" stackId="a" fill={TEMP_COLORS.Cool} />
                    <Bar dataKey="Cold" stackId="a" fill={TEMP_COLORS.Cold} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {/* A/B + Counsellor performance row */}
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <ChartCard
                title="Hero CTA · A/B test"
                subtitle="Register for Scholarship vs Find my slab"
                testid="card-ab"
              >
                <div className="grid grid-cols-2 gap-3">
                  {["A", "B"].map((v) => {
                    const row = data.ab_compare[v];
                    return (
                      <div
                        key={v}
                        className="rounded-lg border border-slate-200 p-4"
                        data-testid={`ab-card-${v}`}
                      >
                        <div className="flex items-center justify-between">
                          <Badge
                            variant="secondary"
                            className={v === "A" ? "bg-[#0A2540] text-white" : "bg-[#C9A961] text-white"}
                          >
                            Variant {v}
                          </Badge>
                          {row.impressions > 0 && (
                            <span className="text-[10px] text-slate-500">
                              {row.impressions} imps
                            </span>
                          )}
                        </div>
                        <p className="font-display mt-2 text-sm font-semibold text-[#0A2540]">
                          "{row.label}"
                        </p>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="text-xl font-bold tabular-nums text-[#0A2540]">
                              {row.register_rate}%
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              Register
                            </div>
                          </div>
                          <div>
                            <div className="text-xl font-bold tabular-nums text-emerald-700">
                              {row.completion_rate}%
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              Quiz done
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ChartCard>

              <ChartCard
                title="Counsellor leaderboard"
                subtitle="Total score within the period"
                testid="card-counsellor-perf"
              >
                {data.counsellor_performance.length === 0 ? (
                  <div className="py-10 text-center text-sm text-slate-500">
                    No team activity in this window
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={data.counsellor_performance}
                      layout="vertical"
                      margin={{ left: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" stroke="#64748B" fontSize={11} />
                      <YAxis dataKey="name" type="category" stroke="#64748B" fontSize={11} width={100} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                      />
                      <Bar dataKey="score" fill="#C9A961" radius={[0, 4, 4, 0]}>
                        {data.counsellor_performance.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? "#0A2540" : "#C9A961"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            <WaTemplateUsage period={period} />

            <p className="mt-8 text-center text-xs text-slate-400">
              Data scope: {user?.role === "super_admin" ? "All tenants" : user?.tenant_name || "Your tenant"} · Period: {period}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
